import React from 'react';
import { ChevronUp, Play, Sparkles, Wand2, FlaskConical, TrendingUp, Smartphone } from 'lucide-react';

const STEPS = [
  { icon: Play, title: 'Scroll', text: 'A Shorts feed built around what you love' },
  { icon: Wand2, title: 'Tap Try', text: 'Recalibrate builds your plan' },
  { icon: FlaskConical, title: 'Learn', text: 'The science behind any video' },
  { icon: TrendingUp, title: 'Track', text: 'How it lifts you, automatically' },
];

const InfoBlock = () => (
  <div
    data-testid="passion-info-block"
    className="relative z-20 -mt-14 mx-3 sm:mx-0 sm:mt-0 sm:absolute sm:-bottom-20 sm:-left-10 sm:w-[23rem] rounded-2xl bg-slate-900/95 backdrop-blur-md text-white p-4 sm:p-5 shadow-2xl shadow-purple-900/40 border border-white/10"
  >
    <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-purple-300 mb-1">Don't feel like tracking?</p>
    <p className="text-sm font-semibold mb-3">Just scroll Shorts. The app does the rest.</p>
    <ul className="grid grid-cols-2 gap-x-3 gap-y-3">
      {STEPS.map(({ icon: Icon, title, text }) => (
        <li key={title} className="flex items-start gap-2" data-testid={`passion-step-${title.toLowerCase().replace(' ', '-')}`}>
          <span className="shrink-0 w-7 h-7 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
            <Icon className="w-3.5 h-3.5" />
          </span>
          <span className="text-[11px] leading-snug text-purple-100/75"><b className="block text-[13px] text-white">{title}</b>{text}</span>
        </li>
      ))}
    </ul>
  </div>
);

const FeedOverlays = () => (
  <>
    <span data-testid="passion-feed-chip" className="absolute top-3 left-3 sm:top-5 sm:left-5 inline-flex items-center gap-1.5 rounded-full bg-black/65 backdrop-blur-md px-3 py-1.5 text-[11px] sm:text-xs font-semibold text-white">
      <Play className="w-3.5 h-3.5 fill-current" /> Passion · Shorts feed
    </span>
    <span data-testid="passion-swipe-hint" className="absolute left-[26%] top-[20%] -translate-x-1/2 flex flex-col items-center gap-1 text-slate-800">
      <span className="w-9 h-9 sm:w-11 sm:h-11 rounded-full bg-white/85 backdrop-blur shadow-lg flex items-center justify-center animate-bounce">
        <ChevronUp className="w-5 h-5 sm:w-6 sm:h-6" />
      </span>
      <span className="hidden sm:block rounded-full bg-white/85 backdrop-blur px-2.5 py-1 text-[11px] font-semibold shadow">Swipe for the next Short</span>
    </span>
    <span data-testid="passion-try-chip" className="absolute left-[52%] top-[68%] -translate-x-1/2">
      <span aria-hidden="true" className="absolute inset-0 rounded-full bg-purple-500/60 animate-ping" />
      <span className="relative inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 px-3 py-1.5 text-[11px] sm:text-sm font-bold text-white shadow-lg ring-2 ring-white/70">
        <Sparkles className="w-3.5 h-3.5" /> Try
      </span>
    </span>
  </>
);

export default function PassionShorts({ visible }) {
  return (
    <section
      id="passion-section"
      data-animate
      data-testid="passion-section"
      className={`relative py-16 sm:py-28 px-4 sm:px-6 bg-gradient-to-br from-slate-50 via-white to-indigo-50 overflow-hidden transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
    >
      <div className="max-w-6xl mx-auto grid lg:grid-cols-12 gap-12 lg:gap-14 items-center">
        <div className="lg:col-span-5 text-center lg:text-left">
          <span data-testid="passion-eyebrow" className="inline-flex items-center gap-1.5 rounded-full bg-orange-100 text-orange-700 ring-1 ring-orange-300/60 px-3 py-1 text-xs font-bold uppercase tracking-[0.16em] mb-5">
            <span aria-hidden="true" className="text-sm leading-none">🎬</span> Passion Feed
            <span data-testid="passion-coming-soon-tag" className="ml-1 rounded-full bg-orange-600 text-white px-2 py-0.5 text-[10px] tracking-[0.12em]">Coming soon</span>
          </span>
          <h2 data-testid="passion-title" className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 leading-tight mb-5">
            Scroll Shorts.{' '}
            <span className="block bg-gradient-to-r from-blue-600 via-purple-600 to-pink-500 bg-clip-text text-transparent">The app does the rest.</span>
          </h2>
          <p data-testid="passion-description" className="text-base sm:text-lg text-slate-600 leading-relaxed mb-8">
            Scroll YouTube Shorts built around what you love. Curate your feed to rediscover your passions. Tap Try on any video and Recalibrate builds your plan, learn the science behind any video and tracks how it lifts you.
          </p>
          <div
            data-testid="passion-coming-soon"
            className="inline-flex items-center justify-center gap-2.5 rounded-2xl border-2 border-dashed border-purple-300 bg-white/80 px-8 py-4 text-purple-900 font-bold text-lg shadow-sm"
          >
            <Smartphone className="w-5 h-5 text-purple-600" aria-hidden="true" />
            <span>Coming soon to app stores</span>
          </div>
        </div>

        <figure className="lg:col-span-7 relative sm:mb-16" data-testid="passion-visual">
          <div aria-hidden="true" className="absolute -inset-3 sm:-inset-4 rounded-[2.4rem] border-2 border-dashed border-purple-300/70 -rotate-2" />
          <div aria-hidden="true" className="absolute -top-8 -right-8 w-40 h-40 bg-pink-300/30 rounded-full blur-3xl" />
          <div className="relative rounded-[2rem] p-[3px] bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 shadow-2xl shadow-purple-500/25">
            <div className="relative rounded-[calc(2rem-3px)] overflow-hidden bg-white">
              <picture>
                <source srcSet="/passion-shorts.webp" type="image/webp" />
                <img
                  src="/passion-shorts.jpg"
                  alt="Recalibrate Passion: a scrollable Shorts feed of watercolour painting, guitar and gardening videos on a phone"
                  width="1264"
                  height="848"
                  loading="eager"
                  decoding="async"
                  data-testid="passion-image"
                  className="block w-full h-auto"
                />
              </picture>
              <FeedOverlays />
            </div>
          </div>
          <InfoBlock />
        </figure>
      </div>
    </section>
  );
}
